// src/services/violationService.ts - Phiên bản cải thiện

import apiClient from "@/api/apiClient";
import {
  WorkDisplayData,
  WorkSearchParams,
  ViolationReportFormData,
  ViolationReportsParams,
  ViolationStats,
  PaginatedResponse,
  ViolationReportWithDetails,
  ID,
  Violation,
} from "@/types";

interface Platform {
  id: number;
  apiIntegration: boolean;
  name: string;
  type: string;
  url: string;
}

interface PlatformDetectionResult {
  platform: Platform | null;
  platformId: number | null;
  platformName: string;
  platformSlug: string;
}

export interface CreateViolationReportData {
  // Basic info
  violationType: string;
  violationUrl: string;
  description: string;
  severity?: 'low' | 'medium' | 'high';
  
  // Reporter info
  reporterRole?: 'author' | 'representative' | 'witness' | 'legal_counsel' | 'other';
  
  // Violator info
  violatorName: string;
  violatorAddress?: string;
  violatorContact?: string;
  violationPlatform?: string;
  
  // Details
  violationDescription?: string;
  impactDescription?: string;
  discoveryDate?: string;
  violationTypes?: string[];
  
  // Evidence
  evidenceItems?: any[];
  evidences?: File[];
  
  // Demands
  demands?: string[];
  
  // Work info
  content?: ID;
  workTitle?: string;
  workAuthor?: string;
  workType?: string;
  copyrightRegistrationNumber?: string;
}

class ViolationService {
  private platformsCache: Platform[] = [];
  private platformsCacheTime: number = 0;
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 phút

  /**
   * Tìm kiếm tác phẩm của người dùng với autocomplete
   */
  //
  async searchWorks(params: WorkSearchParams): Promise<WorkDisplayData[]> {
    try {
      const response = await apiClient.get("/api/contents", {
        params: {
          "filters[$or][0][title][$contains]": params.query,
          "filters[$or][1][description][$contains]": params.query,
          ...(params.contentKind && {
            "filters[contentKind][$eq]": params.contentKind,
          }),
          ...(params.category && {
            "filters[category][id][$eq]": params.category,
          }),
          populate: ["category", "files"],
          "pagination[start]": 0,
          "pagination[limit]": params.limit || 10,
          sort: "title:asc",
        },
      });

      return (response?.data?.data || []).map((item) => ({
        id: item.id,
        title: item.title,
        description: item.description,
        contentKind: item.contentKind,
        category: item.category?.name,
        thumbnail: item.files?.[0]?.url,
        registrationDate: item.registrationDate,
      }));
    } catch (error) {
      console.error("Error searching user works:", error);
      throw error;
    }
  }

  /**
   * Lấy danh sách tác phẩm của người dùng với pagination
   */
  async getUserWorks(
    page = 1,
    limit = 20
  ): Promise<PaginatedResponse<WorkDisplayData>> {
    try {
      const response = await apiClient.get("/api/contents", {
        params: {
          "filters[owners][id][$eq]": "$currentUser",
          populate: ["category", "files"],
          "pagination[page]": page,
          "pagination[pageSize]": limit,
          sort: "createdAt:desc",
        },
      });

      const data = response.data?.data || [];
      const meta = response.data?.meta || {};

      return {
        data: data.map((item) => ({
          id: item.id,
          title: item.attributes.title,
          contentKind: item.attributes.contentKind,
          category: item.attributes.category?.data?.attributes?.name,
          thumbnail: item.attributes.files?.data?.[0]?.attributes?.url,
          registrationDate: item.attributes.registrationDate,
        })),
        pagination: {
          page: meta.pagination?.page || 1,
          pageSize: meta.pagination?.pageSize || limit,
          pageCount: meta.pagination?.pageCount || 1,
          total: meta.pagination?.total || 0,
        },
      };
    } catch (error) {
      console.error("Error fetching user works:", error);
      throw error;
    }
  }

  /**
   * Lấy danh sách platforms được hỗ trợ với caching
   */
  async getSupportedPlatforms(): Promise<Platform[]> {
    try {
      const now = Date.now();
      if (
        this.platformsCache &&
        now - this.platformsCacheTime < this.CACHE_DURATION
      ) {
        return this.platformsCache;
      }

      const response = await apiClient.get("/api/platforms", {
        params: {
          "pagination[limit]": 100,
          sort: "name:asc",
        },
      });

      this.platformsCache = response.data?.data || [];
      this.platformsCacheTime = now;

      return this.platformsCache;
    } catch (error) {
      console.error("Error fetching supported platforms:", error);

      // Fallback platforms với format giống API
      const fallbackPlatforms: Platform[] = [
        {
          id: 1,
          name: "Custom Website",
          url: "https://custom-website.com",
          type: "website",
          apiIntegration: false,
        },
        {
          id: 2,
          name: "Facebook",
          url: "https://facebook.com",
          type: "facebook",
          apiIntegration: false,
        },
      ];

      this.platformsCache = fallbackPlatforms;
      this.platformsCacheTime = now;

      return fallbackPlatforms;
    }
  }

  /**
   * Tự động detect platform từ URL với API data
   */
  async detectPlatformFromUrl(url: string): Promise<Platform | null> {
    try {
      const platforms = await this.getSupportedPlatforms();
      let detectedDomain: string;

      try {
        detectedDomain = new URL(url.trim()).hostname
          .toLowerCase()
          .replace("www.", "");
      } catch {
        // Nếu URL không hợp lệ, trả về platform mặc định
        const websitePlatform =
          platforms.find((p) => p.url === "custom-website.com") || null;
        return websitePlatform;
      }

      // Tìm platform dựa trên domain
      for (const platform of platforms) {
        const domains = platform.url.split(";").map((domain) => {
          const tmp = new URL(domain.trim()).hostname
            .toLowerCase()
            .replace("www.", "");
          return tmp;
        });

        if (
          domains.includes(
            detectedDomain
          ) /*|| detectedDomain.endsWith('.' + domain)*/
        ) {
          return platform;
        }
      }

      // Nếu không tìm thấy, trả về platform "Website"
      const websitePlatform =
        platforms.find((p) => {
          const tmp = new URL(p.url).hostname.toLowerCase();
          return tmp === "custom-website.com";
        }) || null;

      return websitePlatform;
    } catch (error) {
      console.error("Error detecting platform from URL:", error);
      //TODO
      // Fallback detection với hardcode (như code cũ)
      return this.fallbackPlatformDetection(url);
    }
  }

  /**
   * Fallback platform detection khi API không khả dụng
   */
  private fallbackPlatformDetection(url: string): any {
    try {
      const domain = new URL(url).hostname.toLowerCase();

      const platformMap: Record<
        string,
        { name: string; slug: string; id: number }
      > = {
        "facebook.com": { name: "Facebook", slug: "facebook", id: 1 },
        "fb.com": { name: "Facebook", slug: "facebook", id: 1 },
        "instagram.com": { name: "Instagram", slug: "instagram", id: 2 },
        "youtube.com": { name: "YouTube", slug: "youtube", id: 3 },
        "youtu.be": { name: "YouTube", slug: "youtube", id: 3 },
        "tiktok.com": { name: "TikTok", slug: "tiktok", id: 4 },
        "twitter.com": { name: "Twitter", slug: "twitter", id: 5 },
        "x.com": { name: "X (Twitter)", slug: "twitter", id: 5 },
      };

      for (const [key, value] of Object.entries(platformMap)) {
        if (domain.includes(key)) {
          return {
            platform: null, // Không có full platform object
            platformId: value.id,
            platformName: value.name,
            platformSlug: value.slug,
          };
        }
      }

      return {
        platform: null,
        platformId: 6, // Website ID
        platformName: "Website",
        platformSlug: "website",
      };
    } catch {
      return {
        platform: null,
        platformId: 6,
        platformName: "Website",
        platformSlug: "website",
      };
    }
  }

  /**
   * Gửi báo cáo vi phạm với validation cải thiện
   */
  async reportViolation(data: ViolationReportFormData) {
    try {
      try {
        new URL(data.violationUrl);
      } catch {
        throw new Error("URL vi phạm không hợp lệ");
      }

      const violationData = {
        data: {
          content: data.workId,
          platform: data.platform,
          violationType: data.violationType || "other",
          violationUrl: data.violationUrl,
          description: data.description,
          detectedDate: new Date().toISOString(),
          violationStatus: "detected",
          severity: "medium",
          evidenceFiles: data.evidenceFiles,
          identityProtection: data.identityProtection,
          fingerprint: data.fingerprint,
        },
      };

      const violationResponse = await apiClient.post(
        "/api/violations",
        violationData,
        {
          headers: {
            "Content-Type": "application/json",
          },
          timeout: 30000,
        }
      );

      //TODO REVORK
      let uploadFiles = null;
      if (data.evidenceFiles && data.evidenceFiles.length > 0) {
        const formData = new FormData();

        data.evidenceFiles.forEach((file) => {
          formData.append("files", file);
        });

        formData.append("ref", "api::violation.violation");
        formData.append("refId", violationResponse.data.id);
        formData.append("field", "evidences");

        const files = await apiClient.post("/api/upload", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          timeout: 300000, //5 min
        });

        if(!!files?.data?.length) {
          const updateData = {
            data: {
              fileMetadata: files.data
            },
            isCreate: true
          }
          const responseUpdate = await apiClient.put(
            `/api/violations/${violationResponse.data.documentId}`,
            updateData
          );
          uploadFiles = responseUpdate.data.fileMetadata || null;
        }
      }

      return {
        ...violationResponse.data,
        fileMetadata: uploadFiles
      };
    } catch (error) {
      console.error("Error reporting violation:", error);
      throw error;
    }
  }

  /**
   * Lấy danh sách báo cáo vi phạm với filtering và pagination
   */
  async getUserViolationReports(
    params: ViolationReportsParams = {}
  ): Promise<PaginatedResponse<ViolationReportWithDetails>> {
    try {
      const {
        page = 1,
        limit = 20,
        search,
        violationStatus,
        violationType,
        platform,
        sortBy = "createdAt",
        sortOrder = "desc",
        userId,
      } = params;

      const apiParams: any = {
        // 'filters[reporter][id][$eq]': userId,
        populate: ["content", "platform", "evidences", "duplicateReport"],
        "pagination[page]": page,
        "pagination[pageSize]": limit,
        sort: `${sortBy}:${sortOrder}`,
      };

      if (userId) {
        apiParams["filters[appCreatedBy][id][$eq]"] = userId;
      }
      // Search filter
      if (search) {
        apiParams["filters[$or][0][description][$contains]"] = search;
        apiParams["filters[$or][1][content][title][$contains]"] = search;
        apiParams["filters[$or][2][violationUrl][$contains]"] = search;
      }

      // Status filter
      if (violationStatus && violationStatus !== "all") {
        apiParams["filters[violationStatus][$eq]"] = violationStatus;
      }

      // Violation type filter
      if (violationType && violationType !== "all") {
        apiParams["filters[violationType][$eq]"] = violationType;
      }

      // Platform filter
      if (platform && platform !== "all") {
        apiParams["filters[platform][$eq]"] = platform;
      }

      const response = await apiClient.get("/api/violations", {
        params: apiParams,
      });

      const data = response.data?.data || [];
      const meta = response.data?.meta || {};

      return {
        data,
        pagination: {
          page: meta.pagination?.page || 1,
          pageSize: meta.pagination?.pageSize || limit,
          pageCount: meta.pagination?.pageCount || 1,
          total: meta.pagination?.total || 0,
        },
      };
    } catch (error) {
      console.error("Error fetching user violation reports:", error);
      throw error;
    }
  }

  /**
   * Lấy thống kê vi phạm cho dashboard
   */
  async getViolationStats(
    timeRange: "week" | "month" | "quarter" | "year" = "month"
  ): Promise<ViolationStats> {
    try {
      const response = await apiClient.get("/api/violations", {
        params: {
          timeRange,
          userId: "$currentUser",
        },
      });

      return response.data;
    } catch (error) {
      console.error("Error fetching violation stats:", error);

      // Fallback data khi API chưa sẵn sàng
      return {
        totalReports: 0,
        pendingReports: 0,
        resolvedReports: 0,
        rejectedReports: 0,
        avgResolutionDays: 0,
        platformBreakdown: [],
        violationTypeBreakdown: [],
        trendsData: [],
      };
    }
  }

  /**
   * Cập nhật trạng thái báo cáo vi phạm (dành cho admin/legal team)
   */
  async updateViolationReportStatus(
    documentId: ID,
    status: string,
    resolution?: string
  ) {
    try {
      const response = await apiClient.put(`/api/violations/${documentId}`, {
        data: {
          violationStatus: status,
          // processesMetadata: ``,
          ...(resolution && { resolution }),
        },
      });

      return response.data;
    } catch (error) {
      console.error("Error updating violation report status:", error);
      throw error;
    }
  }

  /**
   * Lấy chi tiết báo cáo vi phạm
   */
  async getViolationReport(reportId: ID): Promise<Violation> {
    try {
      const response = await apiClient.get(`/api/violations`, {
        params: {
          "filters[$and][0][id][$eq]": reportId,
          populate: "*"
        },
      });

      const tmp = response?.data?.data?.[0] || null;

      if (!tmp) {
        throw new Error("Không tìm thấy báo cáo");
      }

      return tmp;
    } catch (error) {
      console.error("Error fetching violation report details:", error);
      throw error;
    }
  }

  /**
   * Xóa báo cáo vi phạm (chỉ khi chưa được xử lý)
   */
  async deleteViolationReport(documentId: number) {
    try {
      const response = await apiClient.delete(`/api/violations/${documentId}`);
      return response.data;
    } catch (error) {
      console.error("Error deleting violation report:", error);
      throw error;
    }
  }


  /**
   * Create violation report
   */
  async createViolationReport(
    data: CreateViolationReportData
  ): Promise<any> {
    try {
      // Upload evidences first if any
      let uploadedEvidences = [];
      if (data.evidences && data.evidences.length > 0) {
        uploadedEvidences = await this.uploadEvidences(data.evidences);
      }

      const response = await apiClient.post('/api/violations', {
        data: {
          ...data,
          detectedDate: data.discoveryDate || new Date().toISOString(),
          violationStatus: 'detected',
          evidences: uploadedEvidences.length > 0 ? uploadedEvidences : undefined
        }
      });
      
      return StrapiNormalizer.normalizeSingle(response.data);
    } catch (error) {
      console.error('❌ Failed to create violation report:', error);
      throw error;
    }
  }

  /**
   * Upload evidence files
   */
  private async uploadEvidences(files: File[]): Promise<number[]> {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });

    const response = await apiClient.post('/api/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    return response.data.map((file: any) => file.id);
  }

  /**
   * Get violation by ID
   */
  async getViolation(documentId: string): Promise<any> {
    try {
      const response = await apiClient.get(`/api/violations/${documentId}`, {
        params: {
          populate: [
            'content',
            'platform',
            'evidences',
            'reporter',
            'complaints'
          ]
        }
      });
      
      return StrapiNormalizer.normalizeSingle(response.data);
    } catch (error) {
      console.error('❌ Failed to get violation:', error);
      throw error;
    }
  }

  /**
   * Update violation status
   */
  async updateViolationStatus(
    documentId: string,
    status: string,
    notes?: string
  ): Promise<any> {
    try {
      const response = await apiClient.put(`/api/violations/${documentId}`, {
        data: {
          violationStatus: status,
          ...(notes && {
            actions: {
              timestamp: new Date().toISOString(),
              action: status,
              notes
            }
          })
        }
      });
      
      return StrapiNormalizer.normalizeSingle(response.data);
    } catch (error) {
      console.error('❌ Failed to update violation status:', error);
      throw error;
    }
  }

  /**
   * Get user violations
   */
  async getUserViolations(params?: {
    violationType?: string;
    violationStatus?: string;
    severity?: string;
    page?: number;
    pageSize?: number;
  }): Promise<{
    violations: any[];
    pagination: any;
  }> {
    try {
      const response = await apiClient.get('/api/violations', {
        params: {
          filters: {
            ...(params?.violationType && { violationType: params.violationType }),
            ...(params?.violationStatus && { violationStatus: params.violationStatus }),
            ...(params?.severity && { severity: params.severity })
          },
          pagination: {
            page: params?.page || 1,
            pageSize: params?.pageSize || 25
          },
          populate: ['content', 'platform']
        }
      });
      
      return {
        violations: StrapiNormalizer.normalizeMany(response.data),
        pagination: response.data.meta?.pagination
      };
    } catch (error) {
      console.error('❌ Failed to get violations:', error);
      throw error;
    }
  }

  
}

export default new ViolationService();
